import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import { Tooltip, TooltipShield } from '@/components/ui/tooltip';
import { useFloatingPlacement } from '@/lib/use-floating-placement';
import { cn } from '@/lib/utils';
import { FileText, MessageSquareHeart } from 'lucide-react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';

/**
 * Le petit menu du « + » de fin : « Page de contenu » ou « Page de retour »
 * (retour-experience.md §7.1 ; maquette `timeline-retour.html`, choix 3 ; issue
 * #176).
 *
 * « Absente, elle se réajoute : le "+" en fin de timeline ouvre un petit menu. »
 * Le même menu s'ouvre sur « Ajouter une page » de la scène et du rail : trois
 * déclencheurs, UN panneau, et c'est pourquoi le déclencheur est une fonction
 * que l'appelant dessine - le « + » rond d'une arête, le bouton nommé d'un bout
 * de ligne et la pastille en pointillé du rail ne se ressemblent pas, ce qui
 * s'ouvre sous eux si.
 *
 * ## Le panneau est en `position: fixed`
 *
 * Le rail défile dans sa propre boîte (`overflow-x: auto`, donc `overflow-y`
 * aussi) et la scène aussi : un panneau posé en `absolute` sous « Ajouter une
 * page » y serait rogné, ou ferait défiler la boîte. Il est placé comme la fiche
 * d'une variable (`useFloatingPlacement`), qui ne sort jamais de la fenêtre.
 *
 * ## Le refus se dit au survol, et ne ferme rien
 *
 * Au plafond de 30 pages, « Page de retour » est désactivée avec la raison au
 * survol (§7.1) ; « Page de contenu » l'est aussi, pour la même raison, puisque
 * la page de contenu est refusée au même plafond. Le contrôle est VRAIMENT
 * `disabled` et c'est `TooltipShield` qui le rend survolable : un bouton
 * `disabled` n'émet aucun événement de pointeur.
 *
 * Le reste - Échap, le clic dehors sur `mousedown`, le focus rendu au
 * déclencheur, les flèches - est le régime d'`action-menu.tsx`.
 */

/** What the caller spreads on the node that opens the menu. */
export interface AddPageMenuTrigger {
  readonly ref: (node: HTMLElement | null) => void;
  readonly 'aria-haspopup': 'menu';
  readonly 'aria-expanded': boolean;
  readonly 'aria-controls': string | undefined;
  readonly onClick: () => void;
}

export interface AddPageMenuProps {
  /** The accessible name of the menu, « Ajouter une page ». */
  readonly label: string;
  /** « Une page de blocs, après « Protocole » » - where the content page would land. */
  readonly contentHint: string;
  /** Why « Page de contenu » is closed, or null - the ceiling of 30 pages. */
  readonly contentRefusal: string | null;
  /** Why « Page de retour » is closed, or null - the ceiling of 30 pages. */
  readonly feedbackRefusal: string | null;
  readonly onContent: () => void;
  readonly onFeedback: () => void;
  /** Draws the node that opens the menu. */
  readonly trigger: (props: AddPageMenuTrigger) => ReactNode;
}

/** The width of the panel - fixed, because `useFloatingPlacement` places it on a number. */
const MENU_WIDTH = 272;

export function AddPageMenu({
  label,
  contentHint,
  contentRefusal,
  feedbackRefusal,
  onContent,
  onFeedback,
  trigger,
}: AddPageMenuProps) {
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const container = useRef<HTMLSpanElement>(null);
  const panel = useRef<HTMLDivElement | null>(null);
  const anchorNode = useRef<HTMLElement | null>(null);
  const menuId = useId();

  const holdAnchor = useCallback((node: HTMLElement | null) => {
    anchorNode.current = node;
    setAnchor(node);
  }, []);

  const close = useCallback((refocus: boolean) => {
    setOpen(false);
    if (refocus) anchorNode.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') close(true);
      if (event.key === 'Tab') close(false);
    }
    function onDown(event: MouseEvent) {
      const target = event.target;
      if (target instanceof Node) {
        if (container.current?.contains(target)) return;
        if (panel.current?.contains(target)) return;
      }
      close(false);
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open, close]);

  /** The first open entry takes the focus when the menu opens - see `action-menu.tsx`. */
  useEffect(() => {
    if (!open) return;
    const first = panel.current?.querySelector('[role=menuitem]:not([disabled])');
    if (first instanceof HTMLElement) first.focus();
  }, [open]);

  const { at, panelRef } = useFloatingPlacement({
    anchor: open ? anchor : null,
    width: MENU_WIDTH,
    side: 'bottom',
    align: 'center',
  });

  function moveFocus(event: ReactKeyboardEvent<HTMLDivElement>) {
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
    const enabled = [...(panel.current?.querySelectorAll('[role=menuitem]') ?? [])].filter(
      (node): node is HTMLButtonElement => node instanceof HTMLButtonElement && !node.disabled,
    );
    if (enabled.length === 0) return;
    event.preventDefault();
    const here = enabled.findIndex((node) => node === document.activeElement);
    const last = enabled.length - 1;
    const next =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? last
          : event.key === 'ArrowDown'
            ? here >= last
              ? 0
              : here + 1
            : here <= 0
              ? last
              : here - 1;
    enabled[next]?.focus();
  }

  const entry = (
    kind: 'content' | 'feedback',
    title: string,
    help: string,
    refusal: string | null,
    onPick: () => void,
  ) => {
    const Icon = kind === 'content' ? FileText : MessageSquareHeart;
    const button = (
      <button
        type="button"
        role="menuitem"
        data-testid={EXPERIENCE_IDS.addPageOption}
        data-page-kind={kind}
        disabled={refusal !== null}
        onClick={() => {
          // The focus goes back BEFORE the gesture: the panel unmounts under the
          // item, and the keyboard would land on the document body (see action-menu).
          close(true);
          onPick();
        }}
        className={cn(
          'flex w-full cursor-pointer items-start gap-2.5 rounded-sm px-2.5 py-2 text-left text-gray-900 hover:bg-gray-100 focus-visible:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        )}
      >
        <Icon aria-hidden="true" className="mt-0.5 size-4 shrink-0 text-gray-500" />
        <span className="min-w-0">
          <span className="block font-semibold text-sm leading-5">{title}</span>
          <span className="block text-gray-600 text-xs leading-4">{help}</span>
        </span>
      </button>
    );
    if (refusal === null) return button;
    return (
      <Tooltip label={refusal}>
        <TooltipShield closed className="flex w-full">
          {button}
        </TooltipShield>
      </Tooltip>
    );
  };

  return (
    <span ref={container} className="inline-flex">
      {trigger({
        ref: holdAnchor,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': open ? menuId : undefined,
        onClick: () => setOpen((was) => !was),
      })}
      {open ? (
        <div
          id={menuId}
          ref={(node) => {
            panel.current = node;
            panelRef(node);
          }}
          onKeyDown={moveFocus}
          data-testid={EXPERIENCE_IDS.addPageMenu}
          style={{ left: at.left, top: at.top, width: MENU_WIDTH }}
          className="fixed z-40 max-w-[calc(100vw-16px)] rounded-md border border-gray-200 bg-white p-1 text-left shadow-lg"
        >
          <div role="menu" aria-label={label}>
            {entry('content', 'Page de contenu', contentHint, contentRefusal, onContent)}
            {entry(
              'feedback',
              'Page de retour',
              'Toujours en dernier, avec le gabarit de cinq questions',
              feedbackRefusal,
              onFeedback,
            )}
          </div>
        </div>
      ) : null}
    </span>
  );
}
